import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LOGO_URL, scrollToId } from "../constants";
import { MaskedLine, FrequencyWave } from "./motion";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pt-28 pb-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="glow-pulse absolute left-1/2 top-1/3 h-[42rem] w-[42rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.16)_0%,transparent_62%)]" />
        <div className="absolute left-[12%] top-[18%] h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(22,19,14,0.05)_0%,transparent_65%)]" />
        <FrequencyWave tone="dark" className="absolute bottom-[16%] left-0 h-40 w-full" opacity={0.7} />
        <FrequencyWave tone="dark" className="absolute bottom-[8%] left-0 h-48 w-full" opacity={0.4} />
      </div>

      <motion.div style={{ y: logoY, opacity: fade }} className="relative z-10 flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="ripple-ring absolute -inset-3 rounded-full border border-[#b8912e]/30" />
          <div className="flex items-center justify-center rounded-full bg-[#0a0908] p-8 shadow-[0_25px_80px_rgba(22,19,14,0.35)] ring-1 ring-[#d4af37]/40 sm:p-10">
            <img
              src={LOGO_URL}
              alt="The SEAL Collective — metallic heart emblem with radiant light"
              data-testid="hero-logo"
              className="w-40 object-contain drop-shadow-[0_0_35px_rgba(212,175,55,0.4)] sm:w-48 lg:w-56"
            />
          </div>
        </motion.div>
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 mt-14 text-center">
        <h1
          data-testid="hero-headline"
          className="font-serif-display text-4xl font-light leading-[1.08] tracking-tight text-[#16130e] sm:text-5xl lg:text-6xl"
        >
          <MaskedLine delay={0.5}>BUILT FROM THE HEART.</MaskedLine>
          <MaskedLine delay={0.7}>
            <span className="text-gold-gradient">DESIGNED TO MOVE PEOPLE.</span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={1} className="mt-8">
          <p
            data-testid="hero-subheadline"
            className="mx-auto max-w-xl text-base font-extralight leading-relaxed text-black/65 md:text-lg"
          >
            The SEAL Collective serves individuals and organizations through voice, sound, and human-centered
            transformation.
          </p>
        </MaskedLine>

        <MaskedLine delay={1.15} className="mt-6">
          <p className="mx-auto max-w-2xl text-sm font-extralight leading-relaxed text-black/45">
            Spiritual sound work through Soul in Love. Music and professional voice through Amber Ellis.
            Workplace wellness through Tonal Intentions™.{" "}
            <span className="text-black/60">
              Different expressions. One center.{" "}
              <span className="font-serif-display text-base italic text-[#8a6d1f]">THE HEART.</span>
            </span>
          </p>
        </MaskedLine>

        <MaskedLine delay={1.3}>
          <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              data-testid="hero-explore-button"
              onClick={() => scrollToId("the-collective")}
              className="btn-solid"
            >
              Explore the Collective
            </button>
            <button
              data-testid="hero-work-with-amber-button"
              onClick={() => scrollToId("contact")}
              className="btn-outline"
            >
              Work with Amber
            </button>
          </div>
        </MaskedLine>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("heart-of-work")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-black/35 transition-colors duration-500 hover:text-[#8a6d1f]"
        aria-label="Scroll to next section"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} className="block">
          <ArrowDown size={18} strokeWidth={1} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
